import {
  Typography,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  Button,
  Tooltip,
  Box,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { fetchGitContextCount } from 'modules/git/api/gitApi';
import { listUrlSources } from 'modules/url-sources/api';
import type { UrlSourceListing } from 'modules/url-sources/api/types';
import { SlidersHorizontal, Globe } from 'lucide-react';
import { CHAT_FILTERS } from '../../constants/chat-filters';
import SpecificationSelect from 'components/SpecificationSelect';
import ContextFilesSelect from 'components/ContextFilesSelect';
import type { SourceFiltersState } from '../../hooks/use-chat-filters';
import type { ContextFileSelection } from 'types/contextDocuments';
import styles from './ChatInfoPanel.module.scss';

interface FilterControls {
  sourceFilters: SourceFiltersState;
  toggleFilter: (key: keyof SourceFiltersState) => void;
  toggleContextChat: () => void;
  updateContextMode: (mode: 'all' | 'limit') => void;
  updateContextLimit: (limit: string) => void;
  updateSpecification: (id: string | null) => void;
  toggleSpecificationSelect: () => void;
  updateContextFiles: (selections: ContextFileSelection[]) => void;
  updateUrlSourceIds: (ids: string[]) => void;
}

interface GeneralInfoControls {
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
}

interface UsageData {
  percentage: number;
  sum: number;
  max: number;
}

export interface ChatListItem {
  id: string;
  label: string;
}

interface ChatInfoPanelProps {
  currProject: number | string;
  filterControls: FilterControls;
  generalInfoControls: GeneralInfoControls;
  usageData: UsageData | null;
  reuploadFileNames?: string[] | null;
  onReuploadClick?: () => void;
}

interface RootState {
  reducer: {
    role?: {
      permissions?: Array<{ name: string }>;
    };
  };
}

const ChatInfoPanel = ({
  currProject,
  filterControls,
  generalInfoControls,
  usageData,
  reuploadFileNames,
  onReuploadClick,
}: ChatInfoPanelProps) => {
  const {
    sourceFilters,
    toggleFilter,
    toggleContextChat,
    updateContextMode,
    updateContextLimit,
    updateSpecification,
    toggleSpecificationSelect,
    updateContextFiles,
    updateUrlSourceIds,
  } = filterControls;

  const userRole = useSelector((state: RootState) => state.reducer.role);

  const filteredChatFilters = CHAT_FILTERS.filter((filter) => {
    if (!filter.permission) return true;
    if (!userRole) return false;
    const perms = userRole.permissions;
    if (!Array.isArray(perms)) return false;
    if (perms.length === 0) return true;
    return perms.some((p) => p.name === filter.permission);
  });

  const isGitSourceVisible = useMemo(() => {
    const gitFilter = CHAT_FILTERS.find((f) => f.key === 'use_source_gitLab');
    if (!gitFilter?.permission) return true;
    if (!userRole) return false;
    const perms = userRole.permissions;
    if (!Array.isArray(perms)) return false;
    if (perms.length === 0) return true;
    return perms.some((p) => p.name === gitFilter.permission);
  }, [userRole]);

  const { expanded: expandedGeneralInfo, setExpanded: setExpandedGeneralInfo } =
    generalInfoControls;
  const percentage = usageData?.percentage || 0;
  const sum = usageData?.sum || 0;
  const max = usageData?.max || 160000;

  const getProgressColor = (): 'error' | 'warning' | 'primary' => {
    if (percentage >= 80) return 'error';
    if (percentage >= 60) return 'warning';
    return 'primary';
  };

  const [gitContextCount, setGitContextCount] = useState<{
    count: number;
    total: number;
  } | null>(null);

  useEffect(() => {
    if (!currProject || !isGitSourceVisible) return;

    let cancelled = false;
    fetchGitContextCount(currProject)
      .then((data) => {
        if (!cancelled) setGitContextCount(data);
      })
      .catch(() => {
        if (!cancelled) setGitContextCount(null);
      });

    return () => {
      cancelled = true;
    };
  }, [currProject, isGitSourceVisible]);

  // ─── URL-источники: подгружаем включённые источники проекта ──────────────────
  const [urlSources, setUrlSources] = useState<UrlSourceListing[]>([]);
  const [urlSourcesLoading, setUrlSourcesLoading] = useState(false);
  const [urlSourcesExpanded, setUrlSourcesExpanded] = useState(false);

  useEffect(() => {
    if (!currProject) {
      setUrlSources([]);
      return;
    }
    let cancelled = false;
    setUrlSourcesLoading(true);
    listUrlSources({
      project_id: String(currProject),
      is_enabled: true,
      limit: 200,
    })
      .then((res) => {
        if (!cancelled) setUrlSources(res.items);
      })
      .catch(() => {
        if (!cancelled) setUrlSources([]);
      })
      .finally(() => {
        if (!cancelled) setUrlSourcesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currProject]);

  // Чистим выбор от исчезнувших источников (например, после удаления/выключения в другой вкладке)
  useEffect(() => {
    if (urlSourcesLoading) return;
    const validIds = new Set(urlSources.map((s) => s.id));
    const filtered = (sourceFilters.use_source_url_ids || []).filter((id) => validIds.has(id));
    if (filtered.length !== (sourceFilters.use_source_url_ids?.length || 0)) {
      updateUrlSourceIds(filtered);
    }
  }, [urlSources, urlSourcesLoading, sourceFilters.use_source_url_ids, updateUrlSourceIds]);

  const selectedUrlIds = useMemo(
    () => new Set(sourceFilters.use_source_url_ids || []),
    [sourceFilters.use_source_url_ids]
  );

  const toggleUrlSourceId = useCallback(
    (id: string) => {
      const current = sourceFilters.use_source_url_ids || [];
      const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
      updateUrlSourceIds(next);
    },
    [sourceFilters.use_source_url_ids, updateUrlSourceIds]
  );

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.headerTop}>
          <SlidersHorizontal size={14} className={styles.headerIcon} aria-hidden />
          <Typography component="div" className={styles.headerTitle}>
            Источники информации
          </Typography>
        </div>
        <div className={styles.usageBar}>
          <LinearProgress
            variant="determinate"
            value={Math.min(percentage, 100)}
            color={getProgressColor()}
          />
          <Typography variant="caption" className={styles.usageText}>
            {sum.toLocaleString()} / {max.toLocaleString()} ({percentage.toFixed(2)}%)
          </Typography>
        </div>
        {reuploadFileNames && reuploadFileNames.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
            <Typography variant="caption" color="warning.main" sx={{ flex: 1, minWidth: 0 }} noWrap>
              Файлы требуют перезагрузки: {reuploadFileNames.join(', ')}
            </Typography>
            <Button size="small" variant="text" onClick={onReuploadClick} sx={{ flexShrink: 0 }}>
              Перезагрузить
            </Button>
          </Box>
        )}
      </div>
      <div className={styles.filtersColumn}>
        {filteredChatFilters.map(({ label, key }) => {
          if (key === 'use_source_common') {
            return (
              <Accordion key={key} expanded={expandedGeneralInfo} className={styles.accordion}>
                <AccordionSummary
                  className={styles.accordionSummary}
                  expandIcon={null}
                  onClick={(e) => e.stopPropagation()}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={expandedGeneralInfo}
                        onChange={(e) => {
                          e.stopPropagation();
                          setExpandedGeneralInfo(!expandedGeneralInfo);
                        }}
                      />
                    }
                    label={label}
                    className={`${styles.formLabel} ${styles.generalInfoHeadLabel}`}
                    onClick={(e) => e.stopPropagation()}
                  />
                </AccordionSummary>
                <AccordionDetails className={styles.accordionDetails}>
                  <div className={styles.accordionInner}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={sourceFilters.use_source_common || false}
                          onChange={() => toggleFilter('use_source_common')}
                        />
                      }
                      label="Текст"
                      className={styles.formLabel}
                    />
                    <div className={styles.filesBlock}>
                      <Typography variant="caption" className={styles.filesLabel}>
                        Файлы:
                      </Typography>
                      <ContextFilesSelect
                        projectId={currProject}
                        selected={sourceFilters.use_context_files || []}
                        onChange={updateContextFiles}
                      />
                    </div>
                  </div>
                </AccordionDetails>
              </Accordion>
            );
          }

          const isGit = key === 'use_source_gitLab';
          const filterLabel =
            isGit && gitContextCount !== null ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                {label}
                <Box
                  component="span"
                  sx={{
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    lineHeight: 1,
                    px: 0.75,
                    py: 0.25,
                    borderRadius: '10px',
                    color: 'primary.main',
                    bgcolor: 'primary.main',
                    backgroundImage: 'none',
                    background: (theme) => `${theme.palette.primary.main}18`,
                    border: (theme) => `1px solid ${theme.palette.primary.main}40`,
                    letterSpacing: '0.01em',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {gitContextCount.count}/{gitContextCount.total}
                </Box>
              </Box>
            ) : (
              label
            );

          return (
            <div key={key}>
              <FormControlLabel
                sx={{ marginLeft: '-8px' }}
                control={
                  <Checkbox
                    size="small"
                    checked={
                      key === 'use_specification'
                        ? sourceFilters.show_specification_select
                        : Boolean((sourceFilters as unknown as Record<string, unknown>)[key])
                    }
                    onChange={() => {
                      if (key === 'use_specification') toggleSpecificationSelect();
                      else toggleFilter(key as keyof SourceFiltersState);
                    }}
                  />
                }
                label={filterLabel}
              />
              {key === 'use_specification' && sourceFilters.show_specification_select && (
                <div className={styles.specBlock}>
                  <SpecificationSelect
                    projectId={currProject}
                    value={
                      typeof sourceFilters.use_specification === 'string'
                        ? sourceFilters.use_specification
                        : null
                    }
                    onChange={(id) => updateSpecification(id ? id : null)}
                  />
                </div>
              )}
            </div>
          );
        })}
        <Accordion
          expanded={urlSourcesExpanded}
          onChange={() => setUrlSourcesExpanded((v) => !v)}
          className={styles.accordion}
        >
          <AccordionSummary
            className={styles.accordionSummary}
            expandIcon={null}
            sx={{ cursor: 'pointer' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: '3px' }}>
              <Globe size={16} color="var(--chat-text-secondary)" aria-hidden />
              <Typography component="span" sx={{ fontSize: '0.875rem' }}>
                URL-источники
              </Typography>
              {urlSources.length > 0 && (
                <Box
                  component="span"
                  sx={{
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    lineHeight: 1,
                    px: 0.75,
                    py: 0.25,
                    borderRadius: '10px',
                    color: 'primary.main',
                    background: (theme) => `${theme.palette.primary.main}18`,
                    border: (theme) => `1px solid ${theme.palette.primary.main}40`,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {selectedUrlIds.size}/{urlSources.length}
                </Box>
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails className={styles.accordionDetails}>
            <div className={styles.accordionInner}>
              {urlSourcesLoading ? (
                <Typography variant="caption" color="text.secondary">
                  Загрузка…
                </Typography>
              ) : urlSources.length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                  Нет включённых URL-источников в проекте.
                </Typography>
              ) : (
                <>
                  <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5 }}>
                    <Button
                      type="button"
                      size="small"
                      variant="text"
                      disabled={selectedUrlIds.size === urlSources.length}
                      onClick={() => updateUrlSourceIds(urlSources.map((s) => s.id))}
                      sx={{ minWidth: 0, px: 0.5, fontSize: '0.7rem' }}
                    >
                      Все
                    </Button>
                    <Button
                      type="button"
                      size="small"
                      variant="text"
                      disabled={selectedUrlIds.size === 0}
                      onClick={() => updateUrlSourceIds([])}
                      sx={{ minWidth: 0, px: 0.5, fontSize: '0.7rem' }}
                    >
                      Очистить
                    </Button>
                  </Box>
                  <Box
                    sx={{
                      maxHeight: 220,
                      overflow: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.25,
                    }}
                  >
                    {urlSources.map((source) => (
                      <FormControlLabel
                        key={source.id}
                        sx={{ marginLeft: '-8px', mr: 0 }}
                        control={
                          <Checkbox
                            size="small"
                            checked={selectedUrlIds.has(source.id)}
                            onChange={() => toggleUrlSourceId(source.id)}
                          />
                        }
                        label={
                          <Tooltip title={source.url} placement="right">
                            <Typography
                              variant="body2"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: 220,
                              }}
                            >
                              {source.title || source.url}
                            </Typography>
                          </Tooltip>
                        }
                      />
                    ))}
                  </Box>
                </>
              )}
            </div>
          </AccordionDetails>
        </Accordion>
        <div>
          <FormControlLabel
            sx={{ marginLeft: '-8px' }}
            control={
              <Checkbox
                size="small"
                checked={sourceFilters.use_context_chat || false}
                onChange={toggleContextChat}
              />
            }
            label="Чат"
            className={styles.chatLabel}
          />
          {sourceFilters.use_context_chat && (
            <div className={styles.chatBlock}>
              <Typography variant="caption" className={styles.contextCaption}>
                Количество сообщений из чата для контекста:
              </Typography>
              <Autocomplete
                freeSolo
                size="small"
                options={['Весь чат', '10', '30', '50', '100']}
                value={
                  sourceFilters.context_mode === 'all'
                    ? 'Весь чат'
                    : (sourceFilters.context_messages_limit || 10).toString()
                }
                onChange={(_e, newValue) => {
                  if (newValue === 'Весь чат' || newValue === null) updateContextMode('all');
                  else {
                    updateContextMode('limit');
                    updateContextLimit(newValue ?? '10');
                  }
                }}
                onInputChange={(_e, newInputValue) => {
                  if (newInputValue === 'Весь чат') updateContextMode('all');
                  else if (newInputValue && !isNaN(parseInt(newInputValue, 10))) {
                    updateContextMode('limit');
                    updateContextLimit(newInputValue);
                  }
                }}
                clearOnBlur={false}
                disableClearable={sourceFilters.context_mode === 'all'}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Выберите режим"
                    slotProps={{
                      htmlInput: {
                        ...params.inputProps,
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                      },
                    }}
                  />
                )}
                className={styles.autocomplete}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatInfoPanel;
